import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Activity, Database, TrendingUp, TrendingDown, Brain, BarChart3, DollarSign, Globe, ArrowUpRight, ArrowDownRight, Clock, Wheat, Fuel } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";

// Commodity data
const COMMODITIES = [
  { name: "Cocoa", price: 3974, change: 2.36, type: "soft" },
  { name: "Coffee", price: 2994, change: -1.24, type: "soft" },
  { name: "Wheat", price: 568, change: 1.15, type: "soft" },
  { name: "Gold", price: 2387, change: 2.34, type: "hard" },
  { name: "Crude Oil", price: 81.2, change: -0.95, type: "hard" },
  { name: "Copper", price: 4.62, change: 1.34, type: "hard" },
];

const MARKET_SENTIMENT = [
  { name: "Soft Commodities", value: 67, color: "#00ff41" },
  { name: "Hard Commodities", value: 58, color: "#0ea5e9" },
  { name: "Energy", value: 45, color: "#f97316" },
  { name: "Metals", value: 62, color: "#a855f7" },
];

const chartTheme = { backgroundColor: "transparent", gridColor: "rgba(39,39,42,0.5)", textColor: "#71717a" };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (err) {
        console.error("Failed to load stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-zinc-900 animate-pulse w-48" />
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-900 animate-pulse" />)}
        </div>
      </div>
    );
  }

  const timelineData = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i}:00`,
    value: Math.floor(Math.random() * 500 + 200),
    volume: Math.floor(Math.random() * 100 + 50),
  }));

  return (
    <div data-testid="dashboard-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Good morning!
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Market Intelligence Dashboard | Real-time commodities data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#00ff41] pulse-live" />
          <span className="text-[10px] text-[#00ff41] font-mono uppercase tracking-wider">Markets Open</span>
        </div>
      </div>

      {/* Market Overview Cards */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
            Africa Market Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {COMMODITIES.map((c) => (
              <div key={c.name} className="p-3 bg-zinc-900/50 border border-zinc-800">
                <div className="flex items-center gap-2 mb-1">
                  {c.type === "soft" ? (
                    <Wheat className="w-3.5 h-3.5 text-[#eab308]" />
                  ) : (
                    <Fuel className="w-3.5 h-3.5 text-[#f97316]" />
                  )}
                  <span className="text-[10px] text-zinc-400 uppercase font-mono">{c.name}</span>
                </div>
                <p className="text-lg font-mono font-bold text-zinc-100">${c.price.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  {c.change >= 0 ? (
                    <ArrowUpRight className="w-3 h-3 text-[#00ff41]" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 text-[#ff3b30]" />
                  )}
                  <span className={`text-[10px] font-mono ${c.change >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
                    {c.change >= 0 ? "+" : ""}{c.change}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Heatmap / Activity Chart */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-[#00ff41]" />
                Heatmap of Commodities Trade Flow
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-none text-[9px] border-[#00ff41] text-[#00ff41]">
                  67% Bullish
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={timelineData}>
                <defs>
                  <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ff41" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00ff41" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="hour" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} interval={3} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} width={35} />
                <RechartsTooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Area type="monotone" dataKey="value" stroke="#00ff41" fill="url(#greenGrad)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Global Sentiment */}
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-[#0ea5e9]" />
              Global Market Sentiment
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="64" cy="64" r="56" fill="none" stroke="#27272a" strokeWidth="8" />
                  <circle 
                    cx="64" cy="64" r="56" fill="none" stroke="#00ff41" strokeWidth="8" 
                    strokeDasharray={`${67 * 3.52} ${100 * 3.52}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold text-[#00ff41]">67%</p>
                    <p className="text-[9px] text-zinc-500 uppercase">Bullish</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {MARKET_SENTIMENT.map((s) => (
                <div key={s.name} className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-400 font-mono">{s.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-zinc-800 overflow-hidden">
                      <div className="h-full" style={{ width: `${s.value}%`, backgroundColor: s.color }} />
                    </div>
                    <span className="text-[10px] font-mono" style={{ color: s.color }}>{s.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Health + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Gainers */}
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5 text-[#00ff41]" />
              Top Gainers
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            {COMMODITIES.filter(c => c.change > 0).sort((a, b) => b.change - a.change).slice(0, 4).map((c) => (
              <div key={c.name} className="flex items-center justify-between py-2 border-b border-zinc-900">
                <span className="text-xs text-zinc-300 font-mono">{c.name}</span>
                <div className="text-right">
                  <p className="text-xs font-mono text-zinc-300">${c.price.toLocaleString()}</p>
                  <p className="text-[10px] font-mono text-[#00ff41]">+{c.change}%</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-zinc-500" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ScrollArea className="h-[160px]">
              <div className="space-y-2">
                {(stats?.recent_activity || []).slice(0, 8).map((act, i) => (
                  <div key={i} className="flex items-start gap-3 py-1.5 border-b border-zinc-900">
                    <div className={`w-1.5 h-1.5 mt-1.5 flex-shrink-0 ${act.category === "platform" ? "bg-[#00ff41]" : act.category === "ai" ? "bg-[#0ea5e9]" : act.category === "external" ? "bg-[#f97316]" : "bg-[#06b6d4]"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-zinc-300 font-mono truncate">{act.action}</p>
                      <p className="text-[9px] text-zinc-600 font-mono">{new Date(act.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
